<template>
  <v-container fluid>
    <!-- Title -->
    <v-row no-gutters>
      <v-col cols="12">
        <v-card color="transparent" flat dark>
          <div class="text-h4 font-weight-bold text-center" style="padding: 5vh">Threshold Setting</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Body -->
    <v-row no-gutters>
      <v-col cols="12">
        <v-row align="center" justify="center">
          <v-card rounded="lg" width="90%" class="pa-2">
            <v-card-subtitle style="display: inline-block">Threshold Setting</v-card-subtitle>
            <div style="display: inline-block; float: right" class="mt-4 mr-4">
              <v-btn small depressed color="primary" class="mr-2" @click="SET_NEW_SERVICE_TYPE_DIALOG_VISIBILITY(true)">
                <v-icon left small>mdi-plus</v-icon>
                Add Queue
              </v-btn>
              <v-btn small depressed @click="resetQueue">Reset Current Queues</v-btn>
            </div>

            <v-card-text>
              <!-- items-per-page=-1: the footer is hidden, so without it Vuetify would silently
                   paginate at 10 and make the remaining queues unreachable. -->
              <v-data-table hide-default-footer :items-per-page="-1" :headers="queueSetting.headers" :items="queueSetting.items">
                <!-- Average Waiting Time -->
                <template v-slot:[`item.average_waiting_time`]="{ item }">
                  <v-btn elevation="0" fab small disabled class="ml-2" style="color: black !important">
                    {{ item.average_waiting_time }}
                  </v-btn>
                </template>

                <!-- Queue / Prefix -->
                <template v-slot:[`item.prefix`]="{ item }">
                  <v-text-field
                    v-if="item.prefix_editable === 1"
                    style="max-width: 90px"
                    v-model="item.prefix"
                    @change="changePrefix($event, item)"
                    :rules="[value => prefixRule(value, item)]"
                    dense
                    outlined
                    hide-details="auto"
                  />
                  <span v-else>{{ item.prefix }}</span>
                </template>

                <!-- Booking Enabled -->
                <template v-slot:[`item.booking_enabled`]="{ item }">
                  <v-switch :input-value="item.booking_enabled === 1" @change="changeBookingEnabled($event, item.service_type_id)" hide-details dense class="mt-0 pt-0" />
                </template>

                <!-- Number Start -->
                <template v-slot:[`item.number_start`]="{ item }">
                  <v-text-field type="number" min="1" style="max-width: 70px" v-model="item.number_start" @change="changeNumberStart($event, item.service_type_id)" hide-details dense outlined />
                </template>

                <!-- Number Padding -->
                <template v-slot:[`item.number_padding`]="{ item }">
                  <v-text-field
                    type="number"
                    min="1"
                    max="10"
                    style="max-width: 70px"
                    v-model="item.number_padding"
                    @change="changeNumberPadding($event, item.service_type_id)"
                    hide-details
                    dense
                    outlined
                  />
                </template>

                <!-- Numpad Threshold -->
                <template v-slot:[`item.numpad_threshold`]="{ item }">
                  <v-text-field type="number" style="max-width: 60px" v-model="item.numpad_threshold" @change="updateNumpadThreshold($event, item.service_type_id)" hide-details dense outlined />
                </template>

                <!-- SMS Threshold -->
                <template v-slot:[`item.sms_threshold`]="{ item }">
                  <v-text-field type="number" style="max-width: 60px" v-model="item.sms_threshold" @change="updateSMSThreshold($event, item.service_type_id)" hide-details dense outlined />
                </template>

                <!-- Threshold -->
                <template v-slot:[`item.threshold`]="{ item }">
                  <div class="d-flex align-center my-2">
                    <v-text-field style="max-width: 50px" disabled v-model="item.threshold" hide-details dense outlined :value="item.threshold" />
                    <!-- Narrowed from 250px so the Action column fits without the whole
                         table scrolling sideways and hiding the delete button. -->
                    <v-slider class="px-1" style="width: 150px" @change="changeThreshold(item.service_type_id, item.threshold)" hide-details dense v-model="item.threshold" max="360" />
                  </div>
                </template>

                <!-- Action -->
                <template v-slot:[`item.action`]="{ item }">
                  <!-- U/D/X/T/Z are the built-in in-memory buckets (Unavailable, Done,
                       Processing, Trash, Calling History), not service types; they are never
                       offered for deletion. -->
                  <v-btn v-if="!isReservedQueue(item)" color="error" icon small @click="removeQueue(item)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                  <span v-else class="text-caption grey--text">System</span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-row>
      </v-col>
    </v-row>

    <newServiceTypeDialog />
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import newServiceTypeDialog from '@/components/dialogs/newServiceTypeDialog'
import bookingDisplay from '@/mixins/bookingDisplay'
import { isPrefixReserved, normalizePrefix, validatePrefix } from '@/utils/prefix'
import { queueDeleteConfirmMessage } from '@/utils/queueDeletion'

export default {
  name: 'QueueSetting',
  mixins: [bookingDisplay],
  components: { newServiceTypeDialog },
  computed: {
    ...mapState(['queueSetting', 'general']),
    ...mapGetters(['profilesUsingServiceType'])
  },
  methods: {
    ...mapActions(['resetQueue', 'fetchServiceTypeList', 'fetchBookingProfiles', 'updateServiceTypeThreshold', 'updateServiceTypeField', 'deleteServiceType']),
    ...mapMutations(['SET_SNACKBAR', 'UPDATE_QUEUE_ITEM', 'SET_NEW_SERVICE_TYPE_DIALOG_VISIBILITY']),
    prefixRule(value, item) {
      return validatePrefix(value, this.queueSetting.items, item.service_type_id) || true
    },
    findOriginalPrefix(service_type_id) {
      return this.queueSetting.prefix_baseline[service_type_id]
    },
    revertPrefix(service_type_id) {
      const original = this.findOriginalPrefix(service_type_id)
      this.UPDATE_QUEUE_ITEM({ service_type_id, type: 'prefix', value: original })
    },
    async changePrefix(prefix, item) {
      const service_type_id = item.service_type_id
      // Upper-case here too, not only in the New Queue dialog: ticket codes concatenate the
      // prefix verbatim, so a lower-case rename would start printing `ra001`.
      const trimmed = normalizePrefix(prefix)

      // Format / reserved-word / uniqueness validation, shared with the New Queue dialog
      const prefix_error = validatePrefix(trimmed, this.queueSetting.items, service_type_id)
      if (prefix_error) {
        this.SET_SNACKBAR({ status: true, color: 'error', text: prefix_error, icon: 'close', timeout: '3000' })
        return this.revertPrefix(service_type_id)
      }

      // No local "is this queue busy?" warning: `GET /service_types` is `SELECT * FROM
      // service_types` and that table has no live queue depth, so any such check here reads
      // undefined and never fires. The backend refuses the change outright while tickets are
      // queued under the old prefix ("Cannot change prefix while N ticket(s) remain..."), and
      // that refusal is what the operator sees below.

      const success = await this.updateServiceTypeField({ params: { service_type_id, prefix: trimmed } })
      if (success) {
        // The backend rekeys the (empty) in-memory queue bucket to the new prefix in the same
        // request, so no global queue reset is needed and other live queues are untouched.
        this.SET_SNACKBAR({ status: true, color: 'success', text: 'Prefix updated.', icon: 'check', timeout: '3000' })
        // Refresh so general.queues + the revert baseline reflect the new prefix
        this.fetchServiceTypeList()
      } else {
        // Backend rejected the change (not unique / not editable / queue non-empty) -> revert
        this.revertPrefix(service_type_id)
      }
    },
    async changeBookingEnabled(value, service_type_id) {
      const booking_enabled = value ? 1 : 0
      this.UPDATE_QUEUE_ITEM({ service_type_id, type: 'booking_enabled', value: booking_enabled })
      const success = await this.updateServiceTypeField({ params: { service_type_id, booking_enabled } })
      if (!success) this.fetchServiceTypeList() // revert the optimistic toggle if the save failed
    },
    async changeNumberStart(value, service_type_id) {
      const number_start = Number(value)
      if (Number.isInteger(number_start) && number_start >= 1) {
        const success = await this.updateServiceTypeField({ params: { service_type_id, number_start } })
        if (!success) this.fetchServiceTypeList() // restore server value if the save failed
      } else {
        this.SET_SNACKBAR({ status: true, color: 'error', text: 'Number start must be an integer >= 1.', icon: 'close', timeout: '2000' })
        this.fetchServiceTypeList() // restore the row to its server value, not a hard-coded default
      }
    },
    async changeNumberPadding(value, service_type_id) {
      const number_padding = Number(value)
      // Bound to 1-10, deliberately stricter than the backend's 1-20 (NUMBER_PADDING_MAX):
      // codes are prefix + padStart(number, padding) in a varchar(45) and nothing here needs
      // more than 10. 0 is rejected on both sides — the server answers
      // "Invalid number_padding: must be between 1 and 20" rather than defaulting it.
      if (Number.isInteger(number_padding) && number_padding >= 1 && number_padding <= 10) {
        const success = await this.updateServiceTypeField({ params: { service_type_id, number_padding } })
        if (!success) this.fetchServiceTypeList() // restore server value if the save failed
      } else {
        this.SET_SNACKBAR({ status: true, color: 'error', text: 'Number padding must be an integer between 1 and 10.', icon: 'close', timeout: '2000' })
        this.fetchServiceTypeList() // restore the row to its server value, not a hard-coded default
      }
    },
    isReservedQueue(item) {
      return isPrefixReserved(item.prefix)
    },
    async removeQueue(item) {
      const label = this.serviceTypeDisplay(item.service_type_id)

      // Belt and braces. The button is hidden for these rows and the backend rejects a reserved
      // prefix first of all (evaluate_service_type_deletion), so this only saves a round trip —
      // and keeps the refusal readable if this method is ever reached another way.
      if (this.isReservedQueue(item)) {
        this.SET_SNACKBAR({ status: true, color: 'error', text: `Queue "${label}" is a system-internal queue and cannot be deleted.`, icon: 'close', timeout: '4000' })
        return
      }

      // The backend refuses this case too, but only with a count ("1 booking import profile(s)
      // still target this queue"); checking locally first lets the operator see WHICH profiles
      // to repoint without opening the profiles screen to find out.
      const profiles = this.profilesUsingServiceType(item.service_type_id)
      if (profiles.length) {
        const names = profiles.map(profile => profile.name).join(', ')
        this.SET_SNACKBAR({ status: true, color: 'error', text: `Queue "${label}" is still used by booking profile(s): ${names}. Update those profiles first.`, icon: 'close', timeout: '6000' })
        return
      }

      const confirmed = window.confirm(queueDeleteConfirmMessage(label))
      if (!confirmed) return

      // The action reports the backend's own outcome message (including the cascade counts) and
      // hands it back, so nothing is committed here on the happy path.
      const outcome = await this.deleteServiceType({ params: { service_type_id: item.service_type_id } })
      if (!outcome) return

      // ...but the reload that follows commits its own generic failure message, which would land
      // on top of a SUCCESSFUL deletion and read as "the delete failed". Re-state the backend's
      // outcome verbatim and APPEND the reload problem — the cascade counts are the whole point
      // of this screen's messaging and must not be traded away for a stale-list warning.
      // `null` means a newer reload took over and owns both the list and its own messaging, so
      // this delete says nothing more about it. Only an outright `false` is a stale list.
      const reloaded = await this.fetchServiceTypeList()
      if (reloaded === false) {
        this.SET_SNACKBAR({
          status: true,
          color: 'warning',
          text: `${outcome} (This list could not be reloaded - refresh the page.)`,
          icon: 'alert',
          timeout: '10000'
        })
      }
    },
    changeThreshold(service_type_id, threshold) {
      this.updateServiceTypeThreshold({ params: { service_type_id, threshold } })
    },
    updateNumpadThreshold(threshold, service_type_id) {
      if (Number(threshold) && threshold >= 0) {
        this.updateServiceTypeThreshold({ params: { service_type_id, numpad_threshold: threshold } })
      } else this.UPDATE_QUEUE_ITEM({ service_type_id, type: 'numpad_threshold', value: 0 })
    },
    updateSMSThreshold(threshold, service_type_id) {
      if (Number(threshold) && threshold >= 0) {
        this.updateServiceTypeThreshold({ params: { service_type_id, sms_threshold: threshold } })
      } else this.UPDATE_QUEUE_ITEM({ service_type_id, type: 'sms_threshold', value: 0 })
    }
  },
  mounted() {
    this.fetchServiceTypeList()
    // Needed by removeQueue to refuse deleting a queue a booking profile still routes to.
    this.fetchBookingProfiles()
  }
}
</script>
